import {
  Avatar,
  Box,
  Button,
  Collapsible,
  Flex,
  Icon,
  Image,
  Text,
  Tooltip,
  VStack,
  useDisclosure,
} from '@chakra-ui/react'
import { Modal } from '@/components/shared'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FiPlayCircle,
  FiHelpCircle,
  FiChevronDown,
  FiLayers,
  FiFileText,
  FiZap,
  FiHeadphones,
  FiTruck,
  FiSettings,
  FiSearch,
  FiActivity,
  FiGitPullRequest,
  FiGitMerge,
  FiLogOut,
  FiShield,
  FiBarChart2,
  FiClipboard,
  FiGrid,
  FiFile,
  FiCamera,
  FiTool,
  FiBox,
  FiAlertTriangle,
  FiShare2,
} from 'react-icons/fi'
import { IconType } from 'react-icons'
import { apiFetch, getUser, isAuthorizedUser, trackFeature } from '../../lib/apiFetch'
import { useColorMode } from '../ui/use-color-mode'

interface SubNavItem {
  label: string
  path: string
  icon?: IconType
}

interface NavItem {
  label: string
  path: string
  icon: IconType
  children?: SubNavItem[]
}

// Main navigation items — label values are i18n keys, rendered via t(item.label)
const navigationItems: NavItem[] = [
  {
    label: 'nav.gettingStarted',
    path: '/getting-started',
    icon: FiPlayCircle,
    children: [
      { label: 'nav.testIntent', path: '/getting-started/dsl-guide' },
      { label: 'nav.crossDomain', path: '/getting-started/cross-domain' },
      { label: 'nav.dslRoadmap', path: '/getting-started/dsl-roadmap' },
      { label: 'nav.scenarioContract', path: '/getting-started' },
      { label: 'nav.ftaTour', path: '/getting-started/fta-tour' },
      { label: 'nav.ftaOverview', path: '/fta-overview' },
    ],
  },
  { label: 'nav.faq', path: '/faq', icon: FiHelpCircle },
  { label: 'nav.apis', path: '/api-docs', icon: FiLayers },
  {
    label: 'nav.manufacturingCatalog',
    path: '/manufacturing-catalog',
    icon: FiLayers,
    children: [
      { label: 'nav.evidenceReview', path: '/manufacturing-catalog', icon: FiFileText },
      { label: 'nav.relationshipGraphReview', path: '/manufacturing-catalog/graph', icon: FiShare2 },
    ],
  },
  { label: 'nav.designCatalog', path: '/design-catalog', icon: FiGitMerge },
  { label: 'nav.convertToMarkdown', path: '/auto-kb/convert', icon: FiFileText },
  {
    label: 'nav.developing',
    path: '/ab-test',
    icon: FiTool,
    children: [
      { label: 'nav.abCalibration', path: '/ab-test', icon: FiGitPullRequest },
      { label: 'nav.autoValidation', path: '/auto-validation', icon: FiBarChart2 },
      { label: 'nav.requirementsDoctor', path: '/req-guard', icon: FiHeadphones },
    ],
  },
  {
    label: 'nav.experimental',
    path: '/adas',
    icon: FiBox,
    children: [
      { label: 'nav.adasValidator', path: '/adas', icon: FiShield },
      { label: 'nav.camberVisualizer', path: '/camber-visualizer', icon: FiTruck },
    ],
  },
  {
    label: 'nav.ftaAutomation',
    path: '/fta-automation',
    icon: FiBarChart2,
    children: [
      { label: 'nav.fmeaUpload', path: '/fmea-upload' },
      { label: 'nav.ftaTree', path: '/fta-automation' },
      { label: 'nav.generatedFta', path: '/generated-fta' },
      { label: 'nav.blockDiagramFta', path: '/block-diagram-fta' },
    ],
  },
  { label: 'nav.testGenerator', path: '/auto-test', icon: FiZap },
  {
    label: 'nav.motoSuspension',
    path: '/moto-suspension',
    icon: FiActivity,
    children: [
      { label: 'nav.simulator', path: '/moto-suspension' },
      { label: 'nav.designerWorkshop', path: '/moto-suspension/designer' },
    ],
  },
  {
    label: 'nav.controlPlan',
    path: '/control-plan/import',
    icon: FiClipboard,
    children: [
      { label: 'nav.controlPlanXlsxImport', path: '/control-plan/import', icon: FiGrid },
      { label: 'nav.controlPlanPdfImport', path: '/control-plan/pdf/import', icon: FiFile },
      { label: 'nav.controlPlanVisionPoc', path: '/control-plan-vision-poc', icon: FiCamera },
      { label: 'nav.controlPlanDiffAnalysis', path: '/cp-diff-analysis', icon: FiGitPullRequest },
      { label: 'nav.cpChangeWorkbench', path: '/cp-change-workbench', icon: FiAlertTriangle },
    ],
  },
]

// Routes tracked for analytics but intentionally not shown as top-level nav links
// (dynamic detail pages, or pages reached without a sidebar entry).
const UNTRACKED_NAV_ROUTES: { exact?: string; prefix?: string }[] = [
  { exact: '/poc/trace-completeness' },
  { prefix: '/manufacturing-catalog/' },
  { prefix: '/control-plan/review/' },
  { prefix: '/control-plan/pdf/review/' },
]

interface NavItemComponentProps {
  item: NavItem
  isActive: boolean
}

function NavItemComponent({ item, isActive }: NavItemComponentProps) {
  const { t } = useTranslation()
  return (
    <Link to={item.path}>
      <Flex
        px={4}
        py={2}
        align="center"
        gap={3}
        color={isActive ? 'fg' : 'fg.muted'}
        bg={isActive ? 'bg.surface-2' : 'transparent'}
        _hover={{
          bg: 'bg.surface-2',
          _before: {
            opacity: 1,
          },
        }}
        transition="all 0.2s"
        position="relative"
        _before={{
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: '3px',
          bg: 'brand.500',
          opacity: isActive ? 1 : 0,
          transition: 'opacity 0.2s ease',
        }}
      >
        <Icon as={item.icon} boxSize={5} />
        <Text fontSize="md" fontWeight="normal">
          {t(item.label)}
        </Text>
      </Flex>
    </Link>
  )
}

function isPathActive(path: string, currentPath: string, siblingPaths: string[] = []): boolean {
  if (path === currentPath) return true
  if (currentPath.startsWith(path + '/')) {
    const hasMoreSpecificSibling = siblingPaths.some(
      sibling => sibling !== path && sibling.startsWith(path) && (currentPath === sibling || currentPath.startsWith(sibling + '/'))
    )
    return !hasMoreSpecificSibling
  }
  return false
}

interface NavGroupComponentProps {
  item: NavItem
  currentPath: string
}

function NavGroupComponent({ item, currentPath }: NavGroupComponentProps) {
  const { t } = useTranslation()
  const childPaths = item.children?.map(c => c.path) ?? []
  const isChildActive = item.children?.some(c => isPathActive(c.path, currentPath, childPaths)) ?? false
  const [isOpen, setIsOpen] = useState(isChildActive)

  useEffect(() => {
    if (isChildActive) setIsOpen(true)
  }, [isChildActive])

  return (
    <Box>
      <Flex
        px={4}
        py={2}
        align="center"
        gap={3}
        color={isChildActive ? 'fg' : 'fg.muted'}
        bg={isChildActive ? 'bg.surface-2' : 'transparent'}
        _hover={{ bg: 'bg.surface-2' }}
        cursor="pointer"
        transition="all 0.2s"
        onClick={() => setIsOpen(o => !o)}
      >
        <Icon as={item.icon} boxSize={5} />
        <Text fontSize="md" fontWeight="normal" flex={1}>{t(item.label)}</Text>
        <Icon
          as={FiChevronDown}
          boxSize={4}
          color="fg.subtle"
          transform={isOpen ? 'rotate(0deg)' : 'rotate(-90deg)'}
          transition="transform 0.2s"
        />
      </Flex>
      <Collapsible.Root open={isOpen}>
        <Collapsible.Content>
          <VStack gap={0} align="stretch">
            {item.children?.map(child => {
              const active = isPathActive(child.path, currentPath, childPaths)
              return (
                <Link key={child.path} to={child.path}>
                  <Flex
                    pl={child.icon ? 8 : 12}
                    pr={4}
                    py={1.5}
                    align="center"
                    gap={2}
                    color={active ? 'fg' : 'fg.subtle'}
                    bg={active ? 'bg.surface-2' : 'transparent'}
                    _hover={{ bg: 'bg.surface-2', color: 'fg' }}
                    transition="all 0.15s"
                    position="relative"
                    _before={{
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: '3px',
                      bg: 'brand.500',
                      opacity: active ? 1 : 0,
                      transition: 'opacity 0.2s ease',
                    }}
                  >
                    {child.icon && <Icon as={child.icon} boxSize={4} />}
                    <Text fontSize="sm">{t(child.label)}</Text>
                  </Flex>
                </Link>
              )
            })}
          </VStack>
        </Collapsible.Content>
      </Collapsible.Root>
    </Box>
  )
}

const buildInfo = {
  date: typeof __BUILD_DATE__ !== 'undefined' ? __BUILD_DATE__ : '',
  commit: typeof __BUILD_COMMIT__ !== 'undefined' ? __BUILD_COMMIT__ : '',
}

// Expose in console: type __BUILD_INFO in DevTools
// eslint-disable-next-line @typescript-eslint/no-explicit-any
;(window as any).__BUILD_INFO = buildInfo

export function Sidebar() {
  const location = useLocation()
  const navigate = useNavigate()
  const { i18n, t } = useTranslation()
  const { colorMode, setColorMode } = useColorMode()
  const [showBuildInfo, setShowBuildInfo] = useState(false)
  const [clickCount, setClickCount] = useState(0)
  // #17: Read user on every render so it picks up token changes
  const user = getUser()
  const authorized = isAuthorizedUser()
  const [searchClickCount, setSearchClickCount] = useState(0)
  const { open: isSignOutOpen, onOpen: onSignOutOpen, onClose: onSignOutClose } = useDisclosure()

  // #20: Debounced feature tracking — only track known routes, deduplicate rapid navigation
  const trackedRef = useRef<string>('')
  useEffect(() => {
    const isKnownRoute = navigationItems.some(item =>
      item.path === location.pathname ||
      item.children?.some(c => c.path === location.pathname)
    ) || location.pathname === '/settings' || UNTRACKED_NAV_ROUTES.some(r =>
      r.exact ? r.exact === location.pathname : location.pathname.startsWith(r.prefix!)
    )
    if (isKnownRoute && trackedRef.current !== location.pathname) {
      trackedRef.current = location.pathname
      trackFeature(location.pathname)
    }
  }, [location.pathname])

  useEffect(() => {
    if (clickCount >= 3) {
      setShowBuildInfo(prev => !prev)
      setClickCount(0)
    }
    if (clickCount > 0) {
      const timer = setTimeout(() => setClickCount(0), 600)
      return () => clearTimeout(timer)
    }
  }, [clickCount])

  useEffect(() => {
    if (searchClickCount >= 3) {
      navigate('/user-traffic')
      setSearchClickCount(0)
      return
    }
    if (searchClickCount > 0) {
      const timer = setTimeout(() => setSearchClickCount(0), 600)
      return () => clearTimeout(timer)
    }
  }, [searchClickCount, navigate])

  return (
    <Box
      as="nav"
      w={{ base: 'full', md: '224px' }}
      minW={{ base: 'full', md: '224px' }}
      h="100vh"
      bg="sidebar.bg"
      display={{ base: 'none', md: 'flex' }}
      flexDirection="column"
    >
      {/* Logo — triple-click to toggle build info */}
      <Flex px={6} py={5} align="center">
        <Image
          src="/logo_cypremos.png"
          alt="Cypremos"
          maxH="24px"
          cursor="pointer"
          onClick={() => setClickCount(c => c + 1)}
        />
      </Flex>
      {showBuildInfo && (
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <Text px={4} pb={2} fontSize="2xs" color="fg.faint" fontFamily="mono">
              {new Date(buildInfo.date).toLocaleString()} · {buildInfo.commit.slice(0, 7)}
            </Text>
          </Tooltip.Trigger>
          <Tooltip.Positioner>
            <Tooltip.Content>{t('sidebar.buildMetadata')}</Tooltip.Content>
          </Tooltip.Positioner>
        </Tooltip.Root>
      )}

      {/* Search — triple-click to open user traffic */}
      <Flex
        mx={2}
        mb={2}
        px={4}
        py={2}
        align="center"
        gap={3}
        color="fg.subtle"
        _hover={{
          bg: 'bg.surface-2',
          color: 'fg.muted',
        }}
        cursor="pointer"
        transition="all 0.2s"
        position="relative"
        onClick={() => setSearchClickCount(c => c + 1)}
        _before={{
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: '3px',
          bg: 'brand.500',
          opacity: 0,
          transition: 'opacity 0.2s ease',
        }}
      >
        <Icon as={FiSearch} boxSize={5} />
        <Text fontSize="md">{t('nav.search')}</Text>
      </Flex>

      {/* Navigation */}
      <VStack
        flex="1"
        align="stretch"
        py={2}
        gap={0}
        overflowY="auto"
        css={{
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
        }}
      >
        {(authorized ? navigationItems : navigationItems.filter(i => i.path === '/auto-test')).map(item =>
          item.children ? (
            <NavGroupComponent key={item.label} item={item} currentPath={location.pathname} />
          ) : (
            <NavItemComponent key={item.label} item={item} isActive={item.path === location.pathname} />
          )
        )}
      </VStack>

      {/* Settings at bottom — hidden for unauthorized users */}
      {authorized && (
        <Box py={2}>
          <Text
            px={4}
            py={2}
            color="fg.faint"
            fontSize="xs"
            fontWeight="medium"
            textTransform="uppercase"
            letterSpacing="wider"
          >
            {t('nav.settings')}
          </Text>
          <Link to="/settings">
            <Flex
              px={4}
              py={2}
              align="center"
              gap={3}
              color={location.pathname === '/settings' ? 'fg' : 'fg.muted'}
              bg={location.pathname === '/settings' ? 'bg.surface-2' : 'transparent'}
              _hover={{
                bg: 'bg.surface-2',
                _before: {
                  opacity: 1,
                },
              }}
              transition="all 0.2s"
              position="relative"
              _before={{
                content: '""',
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: '3px',
                bg: 'brand.500',
                opacity: location.pathname === '/settings' ? 1 : 0,
                transition: 'opacity 0.2s ease',
              }}
            >
              <Icon as={FiSettings} boxSize={5} />
              <Text fontSize="md" fontWeight="normal">
                {t('nav.settings')}
              </Text>
            </Flex>
          </Link>
        </Box>
      )}

      {/* Theme toggle */}
      <Flex px={4} py={2} align="center" justify="space-between">
        <Text fontSize="xs" color="fg.faint">{t('sidebar.theme')}</Text>
        <Flex borderWidth="1px" borderColor="border" borderRadius="md" overflow="hidden">
          {(['dark', 'light'] as const).map(mode => (
            <Box
              key={mode}
              as="button"
              px={2}
              py={0.5}
              fontSize="xs"
              bg={colorMode === mode ? 'bg.surface-2' : 'transparent'}
              color={colorMode === mode ? 'fg' : 'fg.faint'}
              _hover={{ bg: 'bg.surface-2' }}
              cursor="pointer"
              onClick={() => setColorMode(mode)}
            >
              {mode === 'dark' ? t('sidebar.theme.dark') : t('sidebar.theme.light')}
            </Box>
          ))}
        </Flex>
      </Flex>

      {/* Language toggle */}
      <Flex px={4} py={2} align="center" justify="space-between">
        <Text fontSize="xs" color="fg.faint">{t('sidebar.language')}</Text>
        <Flex borderWidth="1px" borderColor="border" borderRadius="md" overflow="hidden">
          {(['en', 'ja'] as const).map(lang => (
            <Box
              key={lang}
              as="button"
              px={2}
              py={0.5}
              fontSize="xs"
              bg={i18n.language.startsWith(lang) ? 'bg.surface-2' : 'transparent'}
              color={i18n.language.startsWith(lang) ? 'fg' : 'fg.faint'}
              _hover={{ bg: 'bg.surface-2' }}
              cursor="pointer"
              onClick={() => i18n.changeLanguage(lang)}
            >
              {lang === 'en' ? 'EN' : 'JP'}
            </Box>
          ))}
        </Flex>
      </Flex>

      {/* User + Sign Out */}
      <Box borderTopWidth="1px" borderColor="border" px={4} py={3}>
        <Flex align="center" gap={3}>
          {/* #19: Use name for initials fallback instead of '?' */}
          <Avatar.Root size="xs">
            <Avatar.Image src={user?.avatar || undefined} />
            <Avatar.Fallback name={user?.name || user?.username || undefined} />
          </Avatar.Root>
          <Text fontSize="sm" color="fg.muted" flex={1} lineClamp={1}>
            {user?.name || user?.username || user?.email || 'User'}
          </Text>
          {/* #18: Sign-out with confirmation */}
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <Flex
                as="button"
                onClick={onSignOutOpen}
                color="fg.subtle"
                _hover={{ color: 'fg' }}
                cursor="pointer"
              >
                <Icon as={FiLogOut} boxSize={4} />
              </Flex>
            </Tooltip.Trigger>
            <Tooltip.Positioner>
              <Tooltip.Content>{t('sidebar.signOut')}</Tooltip.Content>
            </Tooltip.Positioner>
          </Tooltip.Root>
        </Flex>
      </Box>

      {/* Sign-out confirmation dialog */}
      <Modal open={isSignOutOpen} onClose={onSignOutClose} bg="bg.surface" borderColor="border" showCloseButton={false} headerProps={{ color: 'fg', fontSize: 'lg' }} bodyProps={{ color: 'fg.muted' }} title={t('sidebar.signOut')} footer={<>
              <Button onClick={onSignOutClose} variant="ghost" color="fg.subtle">
                {t('sidebar.cancel')}
              </Button>
              <Button colorPalette="red" onClick={() => { apiFetch('/auth/logout', { method: 'POST' }).finally(() => window.location.reload()) }}>
                {t('sidebar.signOut')}
              </Button>
            </>} footerProps={{ gap: 3 }}>{t('sidebar.signOutConfirm')}</Modal>
    </Box>
  )
}
